'use client';
import React, { useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { trackPageView } from '@/lib/trackPageView';

const packages = [
  {
    name: 'Basic Bundle',
    price: '$175–$235',
    duration: '≈ 2.5–3 hours',
    tag: null,
    accentColor: '#00f5ff',
    glowColor: 'rgba(0,245,255,0.2)',
    borderColor: 'rgba(0,245,255,0.4)',
    items: [
      'Steam-cleaned seats and hand-washed interior panels',
      'Carpets professionally vacuumed',
      'All glass cleaned inside and out + door jambs cleaned',
      'Trunk vacuumed and air vents dusted',
      'Complimentary air freshener',
      'Exterior foam soak + hand wash',
      'Paint protected with wax + basic wheel clean',
      'Tire shine applied',
    ],
  },
  {
    name: 'Premium Bundle',
    price: '$230–$350',
    duration: '≈ 3–4.5 hours',
    tag: '★ Most Popular',
    accentColor: '#7b2fff',
    glowColor: 'rgba(123,47,255,0.25)',
    borderColor: 'rgba(123,47,255,0.6)',
    items: [
      'Full steam clean of seats with fabric protection or leather conditioning',
      'Deep vacuum + Tornador treatment of carpets, mats, vents, crevices, and trunk',
      'Thorough cleaning of all interior panels, doors, console, and door jambs',
      'Streak-free interior glass',
      'Foam soak + hand wash with iron remover + light clay bar',
      'Wheels cleaned (faces + accessible barrels) with tire shine',
      'Paint protected with quality sealant + exterior trim dressed',
      'Door jambs and exterior glass cleaned',
    ],
  },
  {
    name: 'Luxe Package',
    price: '$330–$430',
    duration: '≈ 4.5–6+ hours',
    tag: null,
    accentColor: '#ff00ff',
    glowColor: 'rgba(255,0,255,0.2)',
    borderColor: 'rgba(255,0,255,0.4)',
    items: [
      'Full steam extraction of seats, carpets, and floor mats',
      'Complete Tornador treatment of vents, crevices, and seat belts',
      'Deep cleaning + conditioning of all interior surfaces',
      'Leather fully conditioned or fabric protected + light odor treatment',
      'Trunk and cargo area fully detailed',
      'Foam soak + multi-stage hand wash with iron remover + full clay bar',
      'Wheels cleaned thoroughly (faces + barrels) with tire + wheel well dressing',
      'Paint hand-enhanced with high-grade sealant',
      'All exterior trim, plastics, and exhaust tips dressed',
      'Full door jambs, fuel door, and exterior glass finished streak-free',
    ],
  },
];

const phaseItems = [
  { label: 'Mobile Detailing', sub: 'Phase 1 — Launching Sept–Oct 2026', color: '#00f5ff', active: true },
  { label: 'Multi-Bay Shop', sub: 'Professional detailing under one roof', color: '#7b2fff', active: false },
  { label: 'Performance Parts & Tuning', sub: 'Power upgrades and custom builds', color: '#ff00ff', active: false },
  { label: 'Wraps & Window Tint', sub: 'Full exterior transformation', color: '#00f5ff', active: false },
];

export default function AboutPage() {
  useEffect(() => {
    trackPageView('/about');
  }, []);

  return (
    <div style={{ background: '#050510', minHeight: '100vh' }}>
      <Header />

      {/* ── Ticker bar ── */}
      <div
        style={{
          background: 'linear-gradient(90deg, #7b2fff, #00f5ff, #ff00ff, #7b2fff)',
          backgroundSize: '300% 100%',
          borderBottom: '1px solid rgba(0,245,255,0.3)',
          overflow: 'hidden',
          padding: '8px 0',
        }}
      >
        <div className="z-ticker-track">
          {[
            '🚗 Vertex Premium',
            '★ Mobile Detailing',
            '🚗 Mesquite, NV',
            '★ Phase 1 Launching Soon',
            '🚗 Las Vegas to St. George',
            '★ We Come to You',
            '🚗 Vertex Premium',
            '★ Mobile Detailing',
            '🚗 Mesquite, NV',
            '★ Phase 1 Launching Soon',
            '🚗 Las Vegas to St. George',
            '★ We Come to You',
          ]?.map((item, i) => (
            <span
              key={i}
              style={{
                fontFamily: 'Barlow Condensed, Arial, sans-serif',
                fontWeight: 900,
                fontSize: '13px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: '#050510',
                padding: '0 24px',
                whiteSpace: 'nowrap',
              }}
            >
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* ── Hero ── */}
      <section
        className="cp-scanlines"
        style={{
          background: 'linear-gradient(135deg, #050510 0%, #0d0d2b 50%, #050510 100%)',
          padding: 'clamp(48px, 10vw, 100px) clamp(16px, 4vw, 48px)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Grid bg */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              linear-gradient(rgba(0,245,255,0.04) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,245,255,0.04) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'absolute', top: '15%', left: '5%', width: '350px', height: '350px', background: 'radial-gradient(circle, rgba(123,47,255,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '10%', right: '8%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(0,245,255,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div className="max-w-7xl mx-auto relative" style={{ zIndex: 2 }}>
          {/* Tag */}
          <div
            className="inline-block px-3 py-1 mb-5"
            style={{ background: 'rgba(123,47,255,0.1)', border: '1px solid #7b2fff', boxShadow: '0 0 12px rgba(123,47,255,0.2)' }}
          >
            <span
              style={{
                fontFamily: 'Barlow Condensed, Arial, sans-serif',
                fontWeight: 900,
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.15em',
                color: '#7b2fff',
                textShadow: '0 0 8px rgba(123,47,255,0.6)',
              }}
            >
              About Vertex Premium
            </span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div className="flex-1 max-w-3xl">
              <h1
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 900,
                  fontSize: 'clamp(48px, 12vw, 110px)',
                  textTransform: 'uppercase',
                  lineHeight: 0.88,
                  letterSpacing: '-0.01em',
                  color: '#e0e8ff',
                  marginBottom: '20px',
                  textShadow: '0 0 40px rgba(0,245,255,0.15)',
                }}
              >
                WHO
                <br />
                <span
                  style={{
                    color: '#00f5ff',
                    WebkitTextStroke: '2px rgba(0,245,255,0.35)',
                    textShadow: '0 0 30px rgba(0,245,255,0.6), 0 0 60px rgba(0,245,255,0.25)',
                  }}
                >
                  WE ARE
                </span>
              </h1>
              <p
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 600,
                  fontSize: 'clamp(15px, 2.2vw, 20px)',
                  color: '#a0b0d8',
                  lineHeight: 1.55,
                  letterSpacing: '0.02em',
                  maxWidth: '620px',
                }}
              >
                We bring premium mobile auto detailing straight to your driveway. Based in{' '}
                <span style={{ color: '#00f5ff', fontWeight: 700 }}>Mesquite, NV</span> and serving the full{' '}
                <span style={{ color: '#00f5ff', fontWeight: 700 }}>Las Vegas → Mesquite → St. George</span> corridor,
                Vertex Premium delivers thorough interior and exterior results using professional-grade equipment and
                products — no shop drop-off required.
              </p>
            </div>

            {/* Water notice card */}
            <div
              style={{
                background: 'rgba(255,0,255,0.06)',
                border: '1px solid rgba(255,0,255,0.35)',
                padding: '20px 24px',
                maxWidth: '320px',
                boxShadow: '0 0 24px rgba(255,0,255,0.1)',
                flexShrink: 0,
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <span style={{ color: '#ff00ff', fontSize: '16px' }}>⚠</span>
                <span
                  style={{
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    color: '#ff00ff',
                    textShadow: '0 0 8px rgba(255,0,255,0.5)',
                  }}
                >
                  Water Access Required
                </span>
              </div>
              <p
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 600,
                  fontSize: '13px',
                  color: 'rgba(224,232,255,0.65)',
                  lineHeight: 1.5,
                  letterSpacing: '0.02em',
                }}
              >
                We do not carry a water tank. A working outdoor spigot or hose bib must be available at your location.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Our Story / Vision ── */}
      <section
        style={{
          background: '#0a0a1e',
          padding: 'clamp(48px, 8vw, 88px) clamp(16px, 4vw, 48px)',
          borderTop: '1px solid rgba(0,245,255,0.08)',
          borderBottom: '1px solid rgba(123,47,255,0.12)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: 0, right: 0, width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(123,47,255,0.07) 0%, transparent 65%)', pointerEvents: 'none' }} />

        <div className="max-w-7xl mx-auto relative" style={{ zIndex: 2 }}>
          {/* Section label */}
          <div className="flex items-center gap-3 mb-8">
            <div style={{ width: '32px', height: '2px', background: '#7b2fff', boxShadow: '0 0 8px rgba(123,47,255,0.6)' }} />
            <span
              style={{
                fontFamily: 'Barlow Condensed, Arial, sans-serif',
                fontWeight: 900,
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.18em',
                color: '#7b2fff',
                textShadow: '0 0 8px rgba(123,47,255,0.5)',
              }}
            >
              Our Vision
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">
            {/* Left — headline */}
            <div className="lg:col-span-2">
              <h2
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 900,
                  fontSize: 'clamp(36px, 7vw, 72px)',
                  textTransform: 'uppercase',
                  lineHeight: 0.9,
                  color: '#e0e8ff',
                  marginBottom: '16px',
                  textShadow: '0 0 30px rgba(123,47,255,0.2)',
                }}
              >
                THIS IS
                <br />
                <span style={{ color: '#7b2fff', textShadow: '0 0 20px rgba(123,47,255,0.7)' }}>PHASE 1</span>
              </h2>
              <div
                style={{
                  background: 'rgba(0,245,255,0.06)',
                  border: '1px solid rgba(0,245,255,0.2)',
                  padding: '12px 16px',
                  display: 'inline-block',
                }}
              >
                <span
                  style={{
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: '#00f5ff',
                    textShadow: '0 0 8px rgba(0,245,255,0.5)',
                  }}
                >
                  Launching Sept–Oct 2026
                </span>
              </div>
            </div>

            {/* Right — text */}
            <div className="lg:col-span-3 flex flex-col gap-5">
              <p
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 600,
                  fontSize: 'clamp(14px, 1.8vw, 17px)',
                  color: '#a0b0d8',
                  lineHeight: 1.65,
                  letterSpacing: '0.02em',
                }}
              >
                Vertex Premium is launching in the next month or two as a fully mobile detailing service. We're starting
                lean and focused on delivering high-quality results one vehicle at a time.
              </p>
              <p
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 600,
                  fontSize: 'clamp(14px, 1.8vw, 17px)',
                  color: '#a0b0d8',
                  lineHeight: 1.65,
                  letterSpacing: '0.02em',
                }}
              >
                The long-term goal is much bigger: a{' '}
                <span style={{ color: '#e0e8ff', fontWeight: 700 }}>full multi-bay shop</span> that specializes in
                professional detailing, continued mobile service, performance parts, tuning, wraps, window tint, and
                more — all under one roof.
              </p>
              <p
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 800,
                  fontSize: 'clamp(15px, 2vw, 19px)',
                  color: '#7b2fff',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  textShadow: '0 0 12px rgba(123,47,255,0.5)',
                }}
              >
                This is just Phase 1.
              </p>
            </div>
          </div>

          {/* Roadmap bento */}
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {phaseItems?.map((phase, i) => (
              <div
                key={i}
                style={{
                  background: phase.active ? `rgba(0,245,255,0.06)` : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${phase.active ? phase.color : 'rgba(255,255,255,0.06)'}`,
                  padding: '18px 16px',
                  position: 'relative',
                  boxShadow: phase.active ? `0 0 20px ${phase.glowColor ?? 'rgba(0,245,255,0.1)'}` : 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                {phase.active && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      width: '7px',
                      height: '7px',
                      borderRadius: '50%',
                      background: '#00f5ff',
                      boxShadow: '0 0 10px #00f5ff, 0 0 20px rgba(0,245,255,0.5)',
                    }}
                  />
                )}
                <div
                  style={{
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.14em',
                    color: phase.active ? phase.color : 'rgba(255,255,255,0.2)',
                    marginBottom: '6px',
                    textShadow: phase.active ? `0 0 8px ${phase.color}80` : 'none',
                  }}
                >
                  Phase {i + 1}
                </div>
                <div
                  style={{
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: 'clamp(13px, 1.8vw, 16px)',
                    textTransform: 'uppercase',
                    color: phase.active ? '#e0e8ff' : 'rgba(224,232,255,0.3)',
                    lineHeight: 1.2,
                    marginBottom: '6px',
                  }}
                >
                  {phase.label}
                </div>
                <div
                  style={{
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 600,
                    fontSize: '11px',
                    color: phase.active ? 'rgba(224,232,255,0.55)' : 'rgba(224,232,255,0.2)',
                    letterSpacing: '0.04em',
                    lineHeight: 1.4,
                  }}
                >
                  {phase.sub}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Service Packages ── */}
      <section
        style={{
          background: '#050510',
          padding: 'clamp(48px, 8vw, 88px) clamp(16px, 4vw, 48px)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Grid bg */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              linear-gradient(rgba(0,245,255,0.025) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,245,255,0.025) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
            pointerEvents: 'none',
          }}
        />

        <div className="max-w-7xl mx-auto relative" style={{ zIndex: 2 }}>
          {/* Section label */}
          <div className="flex items-center gap-3 mb-3">
            <div style={{ width: '32px', height: '2px', background: '#00f5ff', boxShadow: '0 0 8px rgba(0,245,255,0.6)' }} />
            <span
              style={{
                fontFamily: 'Barlow Condensed, Arial, sans-serif',
                fontWeight: 900,
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.18em',
                color: '#00f5ff',
                textShadow: '0 0 8px rgba(0,245,255,0.5)',
              }}
            >
              Service Packages
            </span>
          </div>
          <h2
            style={{
              fontFamily: 'Barlow Condensed, Arial, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(36px, 7vw, 72px)',
              textTransform: 'uppercase',
              lineHeight: 0.9,
              color: '#e0e8ff',
              marginBottom: '8px',
              textShadow: '0 0 30px rgba(0,245,255,0.15)',
            }}
          >
            WHAT WE OFFER
          </h2>
          <p
            style={{
              fontFamily: 'Barlow Condensed, Arial, sans-serif',
              fontWeight: 600,
              fontSize: 'clamp(13px, 1.6vw, 16px)',
              color: 'rgba(160,176,216,0.7)',
              letterSpacing: '0.04em',
              marginBottom: '40px',
            }}
          >
            Pricing varies by vehicle size. Sedans/coupes at the lower end — SUVs/trucks/vans at the higher end. Travel fees may apply outside Mesquite.
          </p>

          {/* Package cards — asymmetric layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {packages?.map((pkg, i) => (
              <div
                key={i}
                style={{
                  background: i === 1 ? `rgba(123,47,255,0.08)` : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${pkg.borderColor}`,
                  padding: '28px 24px',
                  position: 'relative',
                  boxShadow: `0 0 30px ${pkg.glowColor}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-4px)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = `0 8px 40px ${pkg.glowColor}`;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 30px ${pkg.glowColor}`;
                }}
              >
                {/* Most popular badge */}
                {pkg.tag && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '-1px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'linear-gradient(90deg, #7b2fff, #ff00ff)',
                      padding: '4px 16px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'Barlow Condensed, Arial, sans-serif',
                        fontWeight: 900,
                        fontSize: '10px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.14em',
                        color: '#fff',
                      }}
                    >
                      {pkg.tag}
                    </span>
                  </div>
                )}

                {/* Header */}
                <div style={{ marginBottom: '20px', paddingTop: pkg.tag ? '12px' : '0' }}>
                  <h3
                    style={{
                      fontFamily: 'Barlow Condensed, Arial, sans-serif',
                      fontWeight: 900,
                      fontSize: 'clamp(22px, 3vw, 28px)',
                      textTransform: 'uppercase',
                      color: pkg.accentColor,
                      textShadow: `0 0 16px ${pkg.accentColor}80`,
                      marginBottom: '6px',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {pkg.name}
                  </h3>
                  <div className="flex items-baseline gap-3">
                    <span
                      style={{
                        fontFamily: 'Barlow Condensed, Arial, sans-serif',
                        fontWeight: 900,
                        fontSize: 'clamp(28px, 4vw, 38px)',
                        color: '#e0e8ff',
                        lineHeight: 1,
                        textShadow: '0 0 20px rgba(224,232,255,0.2)',
                      }}
                    >
                      {pkg.price}
                    </span>
                  </div>
                  <span
                    style={{
                      fontFamily: 'Barlow Condensed, Arial, sans-serif',
                      fontWeight: 700,
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      color: 'rgba(160,176,216,0.5)',
                    }}
                  >
                    {pkg.duration}
                  </span>
                </div>

                {/* Divider */}
                <div style={{ height: '1px', background: `linear-gradient(90deg, ${pkg.accentColor}40, transparent)`, marginBottom: '20px' }} />

                {/* Items */}
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                  {pkg.items?.map((item, j) => (
                    <li key={j} className="flex items-start gap-2.5">
                      <span
                        style={{
                          color: pkg.accentColor,
                          fontSize: '12px',
                          marginTop: '2px',
                          flexShrink: 0,
                          textShadow: `0 0 6px ${pkg.accentColor}80`,
                        }}
                      >
                        ✦
                      </span>
                      <span
                        style={{
                          fontFamily: 'Barlow Condensed, Arial, sans-serif',
                          fontWeight: 600,
                          fontSize: 'clamp(12px, 1.4vw, 14px)',
                          color: 'rgba(224,232,255,0.7)',
                          lineHeight: 1.45,
                          letterSpacing: '0.02em',
                        }}
                      >
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Pricing note */}
          <div
            className="mt-6 flex items-center gap-3"
            style={{
              background: 'rgba(0,245,255,0.04)',
              border: '1px solid rgba(0,245,255,0.15)',
              padding: '14px 20px',
            }}
          >
            <span style={{ color: '#00f5ff', fontSize: '14px', flexShrink: 0 }}>💧</span>
            <span
              style={{
                fontFamily: 'Barlow Condensed, Arial, sans-serif',
                fontWeight: 700,
                fontSize: '13px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'rgba(224,232,255,0.55)',
              }}
            >
              We come to you. You just need water access.
            </span>
          </div>
        </div>
      </section>

      {/* ── Service Area ── */}
      <section
        style={{
          background: '#0a0a1e',
          padding: 'clamp(40px, 6vw, 72px) clamp(16px, 4vw, 48px)',
          borderTop: '1px solid rgba(0,245,255,0.08)',
          borderBottom: '1px solid rgba(123,47,255,0.1)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '600px', height: '300px', background: 'radial-gradient(ellipse at bottom, rgba(0,245,255,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div className="max-w-7xl mx-auto relative" style={{ zIndex: 2 }}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div style={{ width: '32px', height: '2px', background: '#ff00ff', boxShadow: '0 0 8px rgba(255,0,255,0.6)' }} />
                <span
                  style={{
                    fontFamily: 'Barlow Condensed, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.18em',
                    color: '#ff00ff',
                    textShadow: '0 0 8px rgba(255,0,255,0.5)',
                  }}
                >
                  Service Area
                </span>
              </div>
              <h2
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 900,
                  fontSize: 'clamp(32px, 6vw, 64px)',
                  textTransform: 'uppercase',
                  lineHeight: 0.9,
                  color: '#e0e8ff',
                  marginBottom: '16px',
                }}
              >
                WE COVER THE
                <br />
                <span style={{ color: '#ff00ff', textShadow: '0 0 20px rgba(255,0,255,0.6)' }}>CORRIDOR</span>
              </h2>
              <p
                style={{
                  fontFamily: 'Barlow Condensed, Arial, sans-serif',
                  fontWeight: 600,
                  fontSize: 'clamp(13px, 1.6vw, 16px)',
                  color: 'rgba(160,176,216,0.7)',
                  maxWidth: '480px',
                  lineHeight: 1.55,
                }}
              >
                Based in Mesquite, NV — serving the full Las Vegas to St. George, UT corridor. Travel fees may apply for locations outside the Mesquite area.
              </p>
            </div>

            {/* Corridor visual */}
            <div className="flex flex-col items-center gap-0" style={{ flexShrink: 0 }}>
              {[
                { city: 'Las Vegas, NV', sub: 'Southern end' },
                { city: 'Mesquite, NV', sub: '★ Home Base', highlight: true },
                { city: 'St. George, UT', sub: 'Northern end' },
              ]?.map((stop, i) => (
                <React.Fragment key={i}>
                  <div
                    style={{
                      background: stop.highlight ? 'rgba(0,245,255,0.08)' : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${stop.highlight ? '#00f5ff' : 'rgba(255,255,255,0.08)'}`,
                      padding: '12px 24px',
                      minWidth: '220px',
                      textAlign: 'center',
                      boxShadow: stop.highlight ? '0 0 20px rgba(0,245,255,0.15)' : 'none',
                    }}
                  >
                    <div
                      style={{
                        fontFamily: 'Barlow Condensed, Arial, sans-serif',
                        fontWeight: 900,
                        fontSize: '16px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: stop.highlight ? '#00f5ff' : '#e0e8ff',
                        textShadow: stop.highlight ? '0 0 10px rgba(0,245,255,0.5)' : 'none',
                      }}
                    >
                      {stop.city}
                    </div>
                    <div
                      style={{
                        fontFamily: 'Barlow Condensed, Arial, sans-serif',
                        fontWeight: 600,
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        color: stop.highlight ? 'rgba(0,245,255,0.6)' : 'rgba(224,232,255,0.3)',
                      }}
                    >
                      {stop.sub}
                    </div>
                  </div>
                  {i < 2 && (
                    <div
                      style={{
                        width: '2px',
                        height: '24px',
                        background: 'linear-gradient(180deg, rgba(0,245,255,0.4), rgba(123,47,255,0.4))',
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA / Follow ── */}
      <section
        style={{
          background: 'linear-gradient(135deg, #050510 0%, #0d0d2b 100%)',
          padding: 'clamp(48px, 8vw, 88px) clamp(16px, 4vw, 48px)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, rgba(123,47,255,0.1) 0%, transparent 65%)', pointerEvents: 'none' }} />

        <div className="max-w-4xl mx-auto text-center relative" style={{ zIndex: 2 }}>
          <h2
            style={{
              fontFamily: 'Barlow Condensed, Arial, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(36px, 8vw, 80px)',
              textTransform: 'uppercase',
              lineHeight: 0.9,
              color: '#e0e8ff',
              marginBottom: '16px',
              textShadow: '0 0 40px rgba(123,47,255,0.3)',
            }}
          >
            READY TO
            <br />
            <span style={{ color: '#7b2fff', textShadow: '0 0 30px rgba(123,47,255,0.7)' }}>BOOK?</span>
          </h2>
          <p
            style={{
              fontFamily: 'Barlow Condensed, Arial, sans-serif',
              fontWeight: 600,
              fontSize: 'clamp(14px, 1.8vw, 18px)',
              color: 'rgba(160,176,216,0.7)',
              marginBottom: '32px',
              lineHeight: 1.55,
            }}
          >
            Follow the journey on Instagram{' '}
            <a
              href="https://www.instagram.com/vertexpremiummobiledetailing"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#ff00ff', textDecoration: 'none', fontWeight: 700, textShadow: '0 0 8px rgba(255,0,255,0.5)' }}
            >
              @vertexpremiummobiledetailing
            </a>
            . Ready to book or have questions? Visit{' '}
            <a
              href="https://trueblumobiledetail.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#00f5ff', textDecoration: 'none', fontWeight: 700, textShadow: '0 0 8px rgba(0,245,255,0.5)' }}
            >
              trueblumobiledetail.com
            </a>{' '}
            or reach out for a quote.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/waitlist"
              style={{
                fontFamily: 'Barlow Condensed, Arial Narrow, Arial, sans-serif',
                fontWeight: 900,
                fontSize: '14px',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: '#050510',
                background: '#00f5ff',
                border: '2px solid #00f5ff',
                padding: '14px 32px',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 0 24px rgba(0,245,255,0.5)',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#40faff';
                e.currentTarget.style.boxShadow = '0 0 40px rgba(0,245,255,0.8)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#00f5ff';
                e.currentTarget.style.boxShadow = '0 0 24px rgba(0,245,255,0.5)';
              }}
            >
              <span>★</span> Join the Waitlist
            </Link>
            <a
              href="https://www.instagram.com/vertexpremiummobiledetailing"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontFamily: 'Barlow Condensed, Arial Narrow, Arial, sans-serif',
                fontWeight: 900,
                fontSize: '14px',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: '#ff00ff',
                background: 'transparent',
                border: '2px solid rgba(255,0,255,0.5)',
                padding: '14px 32px',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 0 16px rgba(255,0,255,0.2)',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#ff00ff';
                e.currentTarget.style.boxShadow = '0 0 28px rgba(255,0,255,0.5)';
                e.currentTarget.style.background = 'rgba(255,0,255,0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,0,255,0.5)';
                e.currentTarget.style.boxShadow = '0 0 16px rgba(255,0,255,0.2)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Follow on Instagram
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
